import { any_string } from "./types";

export type ObjectMapContext = any;

export type BuiltInMapKeyTypes = (
  'array'
  | 'path'
  | 'default'
  | 'writeonly'
  | 'readfrom'
  | 'const'
  | 'interpolate'
  | 'context'
  | 'readFrom'
  | 'readonly'
);

// Map Keys
export type MapKeyCommons = {
  dataType?: string
}

export type StringMapKey = string;
export type ConstMapKey = MapKeyCommons & {
  const: string
};
export type InterpolateMapKey = MapKeyCommons & {
  interpolate: string
};
export type ContextMapKey = MapKeyCommons & {
  context: string
};
export type ReadFromMapKey = MapKeyCommons & {
  readFrom: MapKey
  path: MapKey
};
export type ReadonlyMapKey = MapKeyCommons & {
  readonly: MapKey
};

export type WriteonlyMapKey = MapKeyCommons & {
  writeonly: MapKey
};

export type DefaultMapKey = MapKeyCommons & {
  default: MapKey,
  path: MapKey,
};

export type ArrayMapKey = MapKeyCommons & {
  array: MapKey,
  map: ObjectMap,
  initialObject?: any
};


export type CustomMapKey = MapKeyCommons & Record<string, any>;
export type MapKey = (
  StringMapKey
  | ConstMapKey
  | InterpolateMapKey
  | ContextMapKey
  | ReadFromMapKey
  | ReadonlyMapKey
  | WriteonlyMapKey
  | DefaultMapKey
  | ArrayMapKey
  | CustomMapKey
);

// Parsed Map Keys
export type ParsedMapKeyCommons = {
  dataType?: string
}
export type ParsedArrayMapKey = ParsedMapKeyCommons & {
  type: 'array'
  map: ObjectMap
  array: MapKey
  value?: undefined
  initialObject?: any
};
export type ParsedPathMapKey = ParsedMapKeyCommons & {
  type: 'path'
  path: ParsedMapKey
  value: string
};
export type ParsedDefaultMapKey = ParsedMapKeyCommons & {
  type: 'default'
  path: ParsedMapKey
  default: ParsedMapKey
  value: string
};
export type ParsedWriteonlyMapKey = ParsedMapKeyCommons & {
  type: 'writeonly'
  path: ParsedMapKey
  value: string
};
export type ParsedReadfromMapKey = ParsedMapKeyCommons & {
  type: 'readfrom'
  path: ParsedMapKey
  value: string
};

export type ParsedCustomMapKey = ParsedMapKeyCommons & { type: Exclude<string, BuiltInMapKeyTypes>, [key: string] : any };;

export type ParsedMapKey = (
  ParsedArrayMapKey
  | ParsedPathMapKey
  | ParsedDefaultMapKey
  | ParsedWriteonlyMapKey
  | ParsedReadfromMapKey
  | ParsedCustomMapKey
);

export type ParsedPathComponent = {
  accessType: 'array' | 'object',
  attr: string,
  nextAccessType?: string
};
export type ParsedPath = ParsedPathComponent[];
export type ParsedRelativePath_Base = {
  valid: boolean
  absolute?: true | undefined
  relative?: true | undefined
}
export type ParsedRelativePath_Invalid = ParsedRelativePath_Base & {
  valid: false
};
export type ParsedRelativePath_Absolute = ParsedRelativePath_Base & {
  absolute: true
  path: string[]
}
export type ParsedRelativePath_Relative = ParsedRelativePath_Base & {
  relative: true,
  path: string[]
  ancestor: number
};
export type ParsedRelativePath_Valid = (ParsedRelativePath_Absolute | ParsedRelativePath_Relative);

export type ParsedRelativePath = (ParsedRelativePath_Invalid | ParsedRelativePath_Absolute | ParsedRelativePath_Relative)


export function pathsAreEqual(pathA: ParsedRelativePath, pathB: ParsedRelativePath) {
  if (pathA.valid !== pathB.valid) return false;
  if (pathA.absolute) {
    if (!pathB.absolute) return false;
    const aPA = pathA as ParsedRelativePath_Absolute, aPB = pathB as ParsedRelativePath_Absolute;
    if (aPA.path.length !== aPB.path.length) return false;
    if (aPA.path.some((x, i) => x !== aPB.path[i])) return false;
  } else if (pathA.relative) {
    if (!pathB.relative) return false;
    const rPA = pathA as ParsedRelativePath_Relative, rPB = pathB as ParsedRelativePath_Relative;
    if (rPA.ancestor !== rPB.ancestor) return false;
    if (rPA.path.length !== rPB.path.length) return false;
    if (rPA.path.some((x, i) => x !== rPB.path[i])) return false;
  } else {
    return false;
  }

  return true;
}

// Other

export type CustomPathImpl = {
  parse: (mapKey: MapKey) => ParsedMapKey,
  resolve: (pv: MapKey, pk: MapKey, object: any, initialObject: any, context: ObjectMapContext) => any,
}


export type ObjectMap = [MapKey, MapKey][] | Record<string, MapKey>;

export type ObjectMapping = {depth: number, pk: ParsedMapKey, pv: ParsedMapKey};

export function invertMap(map: ObjectMap, context?: ObjectMapContext): ObjectMap {
  if (!Array.isArray(map)) {
    map = Object.entries(map);
  }

  return map
    .map(mapping => {
      if (!Array.isArray(mapping)) return null;
      const [key, value] = mapping;
      const pv = parseMapKey(value);
      if (!pv) return null;

      if (pv.type === 'array') {
        const imap = invertMap(pv.map);
        return [
          pv.array,
          {
            array: key,
            map: imap,
            initialObject: mapObject(pv.initialObject, pv.map, undefined, context),
          },
        ];
      }

      return [value, key];
    })
    .filter(x => !!x) as [MapKey, MapKey][];
}

export function interpolate(text: string, scope: Object | ((interpolation: string) => any)) {
  const fn = typeof scope === 'function' ? (...m: string[]) => scope(m[2] || m[3]) || '' : (...m: string[]) => getObject(scope, m[2] || m[3]) || '';

  return text.replace(/\$(([a-zA-Z]+)|{([^}]+)})/g, fn);
}

export function interpolateMap(map: ObjectMap, scope: any) {
  map = Array.isArray(map) ? map : Object.entries(map);
  return map.map((kv: any[]) =>
    kv.map((k: MapKey): ParsedMapKey | null => {
      const parsed = parseMapKey(k);
      switch (parsed?.type) {
        case 'path':
          return { type: 'path', value: interpolate(parsed.value, scope) };
        case 'const':
          return parsed;
        case 'array':
          return {
            type: 'array',
            array: interpolate(parsed.array, scope),
            map: interpolateMap(parsed.map, scope),
            initialObject: parsed.initialObject,
          };
        default:
          return parsed;
      }
    })
  ).filter(([k, v]) => k !== null && v !== null) as [ParsedMapKey, ParsedMapKey][];
}

/**
 * Creates an object by mapping the fields of a form object onto an entity schema,
 * using the object map derived from map:field parameters in the form schema.
 *
 * Implements the effects of various map:field options, such as:
 *
 * Example "map:field" value:
 * ```
 * {
 *     "default": {
 *         "context": "auth.organizationalUnits[0].id"
 *     },
 *     "path": "data.orgUnitSelector",
 *     "dataType": "number"
 * }
 * ```
 *
 * @param {Object} object - The form object to map fields for.
 * @param {Object} map - The object map. Derived by `parseObjectMapFromSchema`, and found in parsed form definitions.
 * @param {Object} [initialObject]
 * @param {any} [context] - Optional context.
 * @returns {Object} A new object populated by the mapped fields.
 */
export function mapObject(object: any, map: ObjectMap, initialObject?: any, context?: any) {
  if (!Array.isArray(map)) {
    map = Object.entries(map);
  }

  let result = initialObject || {};
  let resultType: string | undefined;

  const mappings = map.reduce((_, mapping) => {
    const [key, value] = mapping || [];
    const _pk = parseMapKey(key);
    const pv = parseMapKey(value);
    if (!_pk || !pv) return _;
    let pk = _pk;

    while (
      pk.type === 'default'
      || pk.type === 'writeonly'
      || pk.type === 'readFrom'
    ) pk = pk.path;

    if (pk.type !== 'path' && pk.type !== 'default') {
      // only perform assign for path keys
    } else {
      const depth = (pk.value || '').split(/\.|\[/).length + (pk.value !== '');
      _.push({ depth, pk, pv });
    }

    return _;
  }, [] as ObjectMapping[]);

  mappings.sort(({ depth: depthA }, { depth: depthB }) => depthA - depthB);

  mappings.forEach(({ pk, pv }) => {
    let mappedValue = resolvePath(pv, pk, object, result, context);

    const { dataType = null } = pv;

    if (mappedValue !== null && mappedValue !== undefined) {
      if (dataType) {
        mappedValue = parseDataType(dataType, mappedValue);
      }

      if (pk.value === '') {
        result = mappedValue;
        resultType = undefined;
      } else {
        if (resultType === undefined) {
          resultType = typeof result;
          if (!resultType || resultType !== 'object' || Array.isArray(result)) result = {};
        }
        setObject(result, pk.value, mappedValue);
      }
    }
  });

  return result;
}

function parseDataType<T extends string, V extends any>(
  type: T,
  value: V
): T extends 'number' ? number : V {
  if (type === 'number') {
    return Number(value) as any;
  }
  return value as any;
};

export const CUSTOM_PATHS: Record<string, CustomPathImpl> = {};

export function resolvePath(
  pv: ParsedMapKey,
  pk: ParsedMapKey,
  object: any,
  initialObject?: any,
  context?: any
): any {
  let result = null;
  switch (pv.type) {
    case 'path':
      result = getObject(object, pv.value);
      break;
    case 'default':
      result = resolvePath(pv.path, pk, object, initialObject, context);
      if (!result) {
        result = resolvePath(pv.default, pk, object, initialObject, context);
      }
      break;
    case 'interpolate':
      result = interpolate(pv.value, { object, initialObject, context });
      break;
    case 'context':
      result = getObject(context, pv.value);
      break;
    case 'writeonly':
      result = null;
      break;
    case 'readFrom':
      result = resolvePath(pv.readFrom, pk, object, initialObject, context);
      break;
    case 'readonly':
      result = resolvePath(pv.value, pk, object, initialObject, context);
      break;
    case 'array':
      {
        let array = getObject(object, pv.array);
        if (array === undefined) {
          array = [];
        } else if (!Array.isArray(array)) {
          array = [array];
        }
        let ioArray = getObject(initialObject, pk.value) || [];
        if (ioArray === undefined) {
          ioArray = [];
        } else if (!Array.isArray(ioArray)) {
          ioArray = [ioArray];
        }

        result = array.map(
          (item: any, idx: number) =>
            mapObject(item, pv.map, ioArray[idx], context))
              .filter((item: any) => !isEmptyObject(item)
        );
        if (result.length === 0) {
          result = [];
        }
      }
      break;
    default:
      if (CUSTOM_PATHS[pv.type]) {
        result = CUSTOM_PATHS[pv.type].resolve(pv, pk, object, initialObject, context);
      } else {
        result = pv.value;
      }
      break;
  }
  return result;
}

export function parseMapKey(mapKey: MapKey): ParsedMapKey | null {
  if (typeof mapKey === 'string') {
    return { type: 'path', value: mapKey };
  }
  if (!mapKey) return null;

  const { dataType } = mapKey
  const _mapKey = mapKey as Record<string, any>;

  if (_mapKey.const) {
    const constMapKey = mapKey as ConstMapKey;
    return { type: 'const', value: constMapKey.const, dataType };
  } else if (_mapKey.interpolate) {
    const interpolateMapKey = mapKey as InterpolateMapKey;
    return { type: 'interpolate', value: interpolateMapKey.interpolate, dataType };
  } else if (_mapKey.context) {
    const contextMapKey = mapKey as ContextMapKey;
    return { type: 'context', value: contextMapKey.context, dataType };
  } else if (_mapKey.readFrom) {
    const readFromMapKey = mapKey as ReadFromMapKey;
    return {
      type: 'readFrom',
      readFrom: parseMapKey(readFromMapKey.readFrom),
      path: parseMapKey(readFromMapKey.path),
      dataType,
    };
  } else if (_mapKey.readonly) {
    const readonlyMapKey = mapKey as ReadonlyMapKey;
    return { type: 'readonly', value: parseMapKey(readonlyMapKey.readonly), dataType };
  } else if (_mapKey.writeonly) {
    const writeonlyMapKey = mapKey as WriteonlyMapKey;
    return { type: 'writeonly', path: parseMapKey(writeonlyMapKey.writeonly), dataType };
  } else if (_mapKey.default) {
    const defaultMapKey = mapKey as DefaultMapKey;
    return {
      type: 'default',
      default: parseMapKey(defaultMapKey.default),
      path: parseMapKey(defaultMapKey.path),
      dataType,
    };
  } else if (_mapKey.array && _mapKey.map) {
    const arrayMapKey = mapKey as ArrayMapKey;
    return {
      type: 'array',
      array: arrayMapKey.array,
      map: arrayMapKey.map,
      initialObject: arrayMapKey.initialObject,
      dataType,
    };
  } else {
    const customMapKey = mapKey as CustomMapKey;
    const customType = Object.keys(customMapKey).find(type => CUSTOM_PATHS[type]);

    if (customType) {
      const parsedKey = {...CUSTOM_PATHS[customType].parse(customMapKey)};
      parsedKey.type = customType;
      return parsedKey;
    } else if (customMapKey.type) {
      return customMapKey as ParsedMapKey
    }
  }

  return null;
}

export function parsePath(path: string | ParsedMapKey) {
  if (path === '') return [];
  if (typeof path === "object" && "type" in path && path.type) {
    if (path.type === 'path') {
      path = path.value;
    } else {
      throw new Error('given path key is not a path key.');
    }
  }

  // if (typeof path.split !== 'function') {
  //     console.log("parsePath", path);
  // }

  const parsed = `${path}`.split('.').reduce((_, component) => {
    try {
      component.split('[').forEach((item, index) => {
        const attr = item.split(']')[0];
        const accessType = index ? 'array' : 'object';
        _.push({ accessType, attr });
      });
      return _;
    } catch (err) {
      console.log('ERROR WITH SPLIT', err);
      throw err;
    }
  }, [] as ParsedPath);

  parsed.forEach((component, index) => {
    component.nextAccessType = (parsed[index + 1] || {}).accessType;
  });

  return parsed;
}

export type AbsolutePathExpression = `$${string}`;
export type RelativePathExpression = any_string;
export type PathExpression = AbsolutePathExpression | RelativePathExpression;

export function parseRelPath<T extends ParsedRelativePath>(relpath: T): T;
export function parseRelPath<T extends AbsolutePathExpression>(relpath: T): ParsedRelativePath_Absolute;
export function parseRelPath(relpath: PathExpression | ParsedRelativePath): ParsedRelativePath;
export function parseRelPath(relpath: PathExpression | ParsedRelativePath): ParsedRelativePath {
  if (typeof relpath === "object" && "valid" in relpath) {
    return relpath;
  }
  const m = /^((\$\.)|(\.+))?(\w+(\.\w+)*)?$/.exec(relpath);
  if (m) {
    const p2 = m[4] ? m[4].split('.') : [];
    if (m[2]) {
      return { valid: true, absolute: true, path: p2 };
    } else {
      return {
        valid: true,
        relative: true,
        path: p2,
        ancestor: m[3] ? m[3].length - 1 : 0,
      };
    }
  } else {
    return { valid: false };
  }
}

export function concatenatePaths(firstPath?: string, ...relpaths: (string | ParsedRelativePath | undefined | null)[]): string {
  if (firstPath === undefined && !relpaths.length) {
    return '';
  }

  const first = (firstPath ?? '').split('.');

  return (relpaths || [])
    .reduce((_, relpath) => {
      if (relpath === undefined || relpath === null) return _;
      const parsed = parseRelPath(relpath);
      if (parsed.valid) {
        if (parsed.absolute) return parsed.path;
        let i = parsed.ancestor;
        while (i > 0 && _.length > 0) {
          _.pop();
          i -= 1;
        }
        _.push(...parsed.path);
      }
      return _;
    }, first)
    .join('.');
}

export function splitPath(path: string): [string | null, string | undefined] {
  const components = path.split('.');
  const leaf = components.pop();
  return [components.length ? components.join('.') : null, leaf];
}

export type Object = { [k in any]: any }

export function getObject(object: Object | undefined | null, path: string | ParsedMapKey) {
  const components = parsePath(path);

  while (components.length) {
    const current = components.shift()!;
    let { attr } = current;
    const { accessType } = current;
    if (object !== undefined && object !== null) {
      const indexAttr = parseInt(attr);
      if (accessType === 'array' && indexAttr < 0) {
        attr = object.length + indexAttr;
      }
      object = object[attr];
    }
  }

  return object;
}

export function setObject(object: any, path: string | ParsedMapKey, value: any) {
  if (path === '') {
    Object.assign(object, value);
    return;
  }
  const components = parsePath(path);
  const lastComponent = components.pop();

  if (!lastComponent) { return; } // <-- edge case, parsed path is []

  while (components.length) {
    const { attr, nextAccessType } = components.shift()!;
    let nextObject = object[attr];

    if (nextObject === undefined || nextObject === null) {
      if (nextAccessType === 'array') {
        nextObject = [];
        object[attr] = nextObject;
      } else {
        // if(nextAccessType === 'object'){
        nextObject = {};
        object[attr] = nextObject;
      }
    } else if (nextAccessType === 'array' && !Array.isArray(nextObject)) {
      nextObject = [];
      object[attr] = nextObject;
    } else if (nextAccessType === 'object' && typeof nextObject !== 'object') {
      nextObject = {};
      object[attr] = nextObject;
    }

    object = nextObject;
  }

  const { attr } = lastComponent;
  object[attr] = value;
}

function isObject(value: unknown): value is object {
  return value !== null && !Array.isArray(value) && typeof value === 'object';
}

function isEmptyObject(value: unknown): value is {} {
  return isObject(value) && Object.keys(value).length === 0;
}

export function deleteObject(object: any, path: string | ParsedMapKey) {
  const components = parsePath(path);
  const lastComponent = components.pop();

  if (!lastComponent) { return; } // <-- edge case, parsed path is []

  while (components.length) {
    if (object === undefined || object === null) return;
    const { attr } = components.shift()!;
    object = object[attr];
  }

  if (object) {
    const { attr } = lastComponent;
    delete object[attr];
  }
}

// export function getPath(node, key, parent = '') {

//     if (!parent)
//         return key;

//     const routes = flat(node);
//     const found = Object.keys(routes).find(k => {

//         if (k.match(new RegExp(key)) && k.match(new RegExp(parent)) && k.match(new RegExp(`properties.${key}`))) {
//             if (k.split('.').includes(key) && k.split('.').includes(parent)) {
//                 return k.match(new RegExp(key));
//             }
//         }
//         return false;
//     });

//     return found ? found.replace(new RegExp(`${key}(.*)$`), key) : undefined;
// }
